import { Box } from '@wix/design-system';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useParams, useSearchParams } from 'react-router-dom';

import { getCategoryByUri } from '../../common/api/getCategoryByUri';
import { CategoriesList } from '../../common/components/CategoriesList';
import { Loader } from '../../common/components/Loader';
import { useAnimation } from '../../common/hooks/useAnimation';
import { useCategories } from '../../common/hooks/useCategories';
import { helpCenterSlice } from '../../common/reducers/helpCenter';
import { Subtitle } from '../../help-center/components/Subtitle';
import { appRoutes } from '../../react-router';
import { biHelpCenter } from '../../util/bi';
import { CategoryArticles } from './CategoryArticles';
import s from './styles.scss';

export const Category: React.FC = () => {
  const dispatch = useDispatch();
  const { categoryId, categoryUri } = useParams();
  const {
    categories,
    currentCategory: category,
    normalizedCategories,
  } = useCategories();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeAnimation = useAnimation(s.moveInRight, s.moveInLeft, [
    categoryId,
  ]);

  const fetchCategory = React.useCallback(
    async (id) => {
      if (!categories?.length) {
        return;
      }
      const foundCategory = normalizedCategories[id];
      if (foundCategory) {
        dispatch(helpCenterSlice.actions.setCurrentCategory(foundCategory));
      }
    },
    [categories?.length, dispatch, normalizedCategories],
  );

  React.useEffect(() => {
    if (categoryUri) {
      getCategoryByUri(categoryUri)
        .then((localCategory) =>
          navigate(
            {
              pathname: appRoutes.categoryById({
                categoryId: localCategory.id,
              }),
              search: searchParams.toString(),
            },
            { replace: true },
          ),
        )
        .catch(() => navigate(appRoutes.categories()));
    } else {
      fetchCategory(categoryId);
    }
  }, [categoryId, categories, categoryUri]);

  const sendBackBi = React.useCallback(() => {
    if (!category) {
      return;
    }
    if (category.children?.length) {
      biHelpCenter.logCategorySubCategoryListBackClicked(category);
    } else {
      biHelpCenter.logCategorySolutionListBackClicked(category);
    }
  }, [category]);

  if (!category) {
    return (
      <Loader
        dataHook="category__loader"
        height="100%"
        verticalAlign="middle"
        width="100%"
      />
    );
  }

  return (
    <Box className={activeAnimation} direction="vertical" height="100%">
      <Subtitle text={category.name} sendBackBi={sendBackBi} />
      {category.children?.length ? (
        <CategoriesList categories={category.children} />
      ) : (
        <CategoryArticles category={category} />
      )}
    </Box>
  );
};
